import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Usuario } from '@/types'
import toast from 'react-hot-toast'

export const AuthCallback = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [processando, setProcessando] = useState(true)

  useEffect(() => {
    processarCallback()
  }, [])

  const processarCallback = async () => {
    try {
      setProcessando(true)

      // Ler tokens da URL (backend redireciona com token e refreshToken)
      const token = searchParams.get('token')
      const refreshToken = searchParams.get('refreshToken')
      const success = searchParams.get('success')

      // Verificar se login foi bem-sucedido
      if (success === 'true' && token && refreshToken) {
        // Salvar tokens no localStorage (usando authToken para manter consistência)
        localStorage.setItem('authToken', token)
        localStorage.setItem('refreshToken', refreshToken)

        // Tentar obter dados do usuário da URL (se backend enviar)
        let usuario: Usuario | null = null
        const usuarioStr = searchParams.get('usuario')
        if (usuarioStr) {
          try {
            usuario = JSON.parse(decodeURIComponent(usuarioStr))
            localStorage.setItem('user', JSON.stringify(usuario))
          } catch (e) {
            console.error('Erro ao parsear usuário da URL:', e)
          }
        }

        // Se não houver usuário na URL, extrair dados do token JWT
        if (!usuario) {
          try {
            // Decodificar o token JWT para obter dados do usuário
            // O token JWT tem formato: header.payload.signature
            const tokenParts = token.split('.')
            if (tokenParts.length === 3) {
              try {
                const payload = JSON.parse(atob(tokenParts[1]))
                
                // Criar objeto usuário a partir do payload do token
                // O token contém: id, nome, email (sub), role, ativo
                usuario = {
                  id: payload.id || '',
                  nome: payload.nome || '',
                  email: payload.sub || payload.email || '',
                  role: payload.role || 'USER',
                  ativo: payload.ativo !== undefined ? payload.ativo : true,
                  dataCriacao: payload.iat ? new Date(payload.iat * 1000).toISOString() : new Date().toISOString(),
                  dataAtualizacao: payload.exp ? new Date(payload.exp * 1000).toISOString() : new Date().toISOString(),
                } as Usuario
                
                // Salvar usuário no localStorage
                localStorage.setItem('user', JSON.stringify(usuario))
              } catch (e) {
                console.error('Erro ao decodificar token:', e)
                // Se não conseguir decodificar, o AuthContext vai tentar buscar depois
              }
            }
          } catch (error) {
            console.error('Erro ao processar token:', error)
            // Se não conseguir processar, o AuthContext vai tentar buscar depois
          }
        }

        // Disparar evento de login para atualizar contexto (com usuário se disponível)
        if (usuario) {
          window.dispatchEvent(new CustomEvent('auth-login', { 
            detail: usuario 
          }))
        } else {
          // Se não temos usuário, disparar evento sem detail
          // O AuthContext vai buscar do localStorage ou do backend
          window.dispatchEvent(new CustomEvent('auth-login'))
        }
        
        toast.success('Login realizado com sucesso!', {
          duration: 5000
        })

        // Aguardar um pouco para garantir que o contexto processou o evento
        // O AuthContext precisa processar o evento e atualizar o estado
        await new Promise(resolve => setTimeout(resolve, 500))

        // Redirecionar para página principal
        navigate('/', { replace: true })
        return
      }

      // Se não foi sucesso, verificar se há erro
      const error = searchParams.get('error')
      if (error) {
        toast.error(decodeURIComponent(error) || 'Erro ao fazer login', {
          duration: 8000
        })
        navigate('/login', { replace: true })
        return
      }

      // Se não há tokens nem erro, algo deu errado
      toast.error('Erro ao processar login. Tente novamente.', {
        duration: 8000
      })
      navigate('/login', { replace: true })

    } catch (error: any) {
      console.error('Erro ao processar callback OAuth2:', error)
      
      const errorMessage = error?.response?.data?.erro || 
                         error?.message || 
                         'Erro ao processar login com Google'
      
      // Tratar erros específicos
      if (errorMessage.toLowerCase().includes('inativo')) {
        toast.error('Usuário inativo. Entre em contato com o administrador.', {
          duration: 8000
        })
      } else if (errorMessage.toLowerCase().includes('aprovado') || 
                 errorMessage.toLowerCase().includes('aprovação')) {
        toast.error('Usuário criado com sucesso, mas precisa ser aprovado por um administrador antes de fazer login.', {
          duration: 10000
        })
      } else {
        toast.error(errorMessage, {
          duration: 8000
        })
      }

      navigate('/login', { replace: true })
    } finally {
      setProcessando(false)
    }
  }


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600">
          {processando ? 'Processando login...' : 'Redirecionando...'}
        </p>
      </div>
    </div>
  )
}

export default AuthCallback

